import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PlatformAdminListItem } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';

export function PlatformAdminsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['platform', 'admins'],
    queryFn: () => api<PlatformAdminListItem[]>('/platform/admins'),
  });
  const platformAdmins = data?.filter((a) => a.isPlatformAdmin) ?? [];

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [manualPassword, setManualPassword] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Record<string, string>>({});

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  const create = useMutation({
    mutationFn: () => api('/platform/admins', { method: 'POST', body: { email, password: password || undefined } }),
    onSuccess: () => {
      setEmail('');
      setPassword('');
      invalidate();
    },
    onError: (e) => setCreateError(e instanceof ApiError ? e.message : 'Fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api(`/platform/admins/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (e, userId) =>
      setResult((m) => ({ ...m, [userId]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });

  const resetEmail = useMutation({
    mutationFn: (userId: string) =>
      api(`/platform/admins/${userId}/reset-password`, { method: 'POST', body: { mode: 'email' } }),
    onSuccess: (_r, userId) => setResult((m) => ({ ...m, [userId]: 'E-Mail zum Zurücksetzen verschickt.' })),
    onError: (e, userId) =>
      setResult((m) => ({ ...m, [userId]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });
  const resetManual = useMutation({
    mutationFn: ({ userId, password: p }: { userId: string; password: string }) =>
      api(`/platform/admins/${userId}/reset-password`, { method: 'POST', body: { mode: 'manual', password: p } }),
    onSuccess: (_r, { userId }) => {
      setManualPassword((m) => ({ ...m, [userId]: '' }));
      setResult((m) => ({ ...m, [userId]: 'Neues Passwort gesetzt.' }));
    },
    onError: (e, { userId }) =>
      setResult((m) => ({ ...m, [userId]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Plattformadmins</h1>

      <Card title="Plattformadmin anlegen">
        {createError && <Alert>{createError}</Alert>}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setCreateError(null);
            create.mutate();
          }}
        >
          <Field label="E-Mail">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Passwort (bei neuem Konto erforderlich)">
            <Input
              type="text"
              placeholder="mind. 10 Zeichen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            Anlegen
          </Button>
        </form>
        <p className="mt-2 text-xs text-slate-400">
          Existiert bereits ein Konto mit dieser E-Mail, wird es zum Plattformadmin erhoben — sein
          Passwort bleibt unverändert.
        </p>
      </Card>

      <Card title={`${platformAdmins.length} Plattformadmins`}>
        <div className="divide-y divide-slate-100">
          {platformAdmins.map((a) => (
            <div key={a.userId} className="py-3">
              {/* Zwei kompakte Zeilen statt einer langen `flex-wrap`-Zeile
               * aus fünf vollformatigen Elementen (§ Nutzeranfrage „restliche
               * Seiten … in der mobilen Ansicht anpassen") — die volle
               * Button-Größe plus ein fest `w-56` breites Eingabefeld
               * stapelten sich auf Handy-Breite zu 4-5 beengten Zeilen.
               * Identität oben, kompakte Aktions-Buttons (`text-xs px-2
               * py-1`, gleiche Größe wie auf PlatformUsersPage) darunter,
               * Passwort-Feld + „manuell setzen" als eigene, zusammengehörige
               * Zeile (Feld nimmt auf Mobile die volle Breite, ab `sm:`
               * wieder die feste Breite wie zuvor). */}
              <div className="min-w-0">
                <div className="truncate font-medium">{a.email}</div>
                <div className="text-xs text-slate-500">{a.status}</div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => {
                    setResult((m) => ({ ...m, [a.userId]: '' }));
                    resetEmail.mutate(a.userId);
                  }}
                  disabled={resetEmail.isPending}
                >
                  Passwort per E-Mail zurücksetzen
                </Button>
                <Button
                  variant="danger"
                  className="px-2 py-1 text-xs"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(`„${a.email}" als Plattformadmin entfernen?`)) {
                      setResult((m) => ({ ...m, [a.userId]: '' }));
                      remove.mutate(a.userId);
                    }
                  }}
                >
                  Entfernen
                </Button>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Input
                  type="text"
                  placeholder="neues Passwort (mind. 10 Zeichen)"
                  className="w-full min-w-0 flex-1 px-2.5 py-1.5 text-xs sm:w-56 sm:flex-none"
                  value={manualPassword[a.userId] ?? ''}
                  onChange={(e) => setManualPassword((m) => ({ ...m, [a.userId]: e.target.value }))}
                />
                <Button
                  variant="ghost"
                  className="shrink-0 px-2 py-1 text-xs"
                  disabled={(manualPassword[a.userId]?.length ?? 0) < 10 || resetManual.isPending}
                  onClick={() => {
                    setResult((m) => ({ ...m, [a.userId]: '' }));
                    resetManual.mutate({ userId: a.userId, password: manualPassword[a.userId]! });
                  }}
                >
                  manuell setzen
                </Button>
              </div>
              {result[a.userId] && <p className="mt-1.5 text-xs text-slate-500">{result[a.userId]}</p>}
            </div>
          ))}
          {platformAdmins.length === 0 && <p className="py-2 text-sm text-slate-500">Keine Plattformadmins.</p>}
        </div>
      </Card>
    </div>
  );
}
